import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { newCampaignActions, notificationsActions } from '../../../../../store';
import AsyncSelect from 'react-select/async';
import { getRandomHex, hexaToRgb } from '../../../../../utils/functions';
const colorsArr = hexaToRgb(getRandomHex(15));
let debounceLoadOptions;

const InputZoneSearch = (props) => {
    const { radius = 0, id, locationForFilters = null } = props;
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const reduxLocation = useSelector((state) => state.newCampaign.location);
    const locationFilterSource = locationForFilters || reduxLocation;
    const random = Math.floor(Math.random() * colorsArr.length);
    const randomRgb = colorsArr[random];
    const selectedCountries = locationFilterSource?.selectedCountries || [];
    const countriesCatalog = locationFilterSource?.countries || [];
    const selectedCountryCodes = selectedCountries
        .map((country) => {
            const directCode = String(
                country?.iso2 ||
                    country?.value ||
                    country?.code2 ||
                    country?.country_code ||
                    ''
            )
                .trim()
                .toUpperCase();
            if (/^[A-Z]{2}$/.test(directCode)) return directCode;

            const selectedId = String(
                country?.id ?? country?.country_id ?? country?.code ?? ''
            ).trim();
            const matchedCountry = countriesCatalog.find((catalogCountry) => {
                const catalogId = String(
                    catalogCountry?.id ??
                        catalogCountry?.country_id ??
                        catalogCountry?.code ??
                        ''
                ).trim();
                return selectedId && catalogId && selectedId === catalogId;
            });
            const fallbackCode = String(
                matchedCountry?.iso2 ||
                    matchedCountry?.value ||
                    matchedCountry?.code2 ||
                    matchedCountry?.country_code ||
                    ''
            )
                .trim()
                .toUpperCase();
            return /^[A-Z]{2}$/.test(fallbackCode) ? fallbackCode : '';
        })
        .filter((code) => /^[A-Z]{2}$/.test(code));
    const selectedCountryNames = selectedCountries
        .map((country) =>
            String(country?.name || country?.label || country?.value || '').trim()
        )
        .filter(Boolean);

    const notExists = (search) => {
        return !reduxLocation.zones.some((zone) => zone.search === search);
    };

    const checkColorAlreadyExist = (zone) => {
        return reduxLocation.zones.some(
            (locationZone) => locationZone.results[0].color == zone.color
        );
    };

    const generateColorAgain = () => {
        const random = Math.floor(Math.random() * colorsArr.length);
        const randomRgb = colorsArr[random];
        return randomRgb;
    };

    const getCountryCodeFromGeocodeResult = (geoResult) => {
        const components = Array.isArray(geoResult?.address_components)
            ? geoResult.address_components
            : [];
        const countryComponent = components.find((component) =>
            Array.isArray(component?.types)
                ? component.types.includes('country')
                : false
        );
        return String(countryComponent?.short_name || '')
            .trim()
            .toUpperCase();
    };

    const resolveCountryCode = async (result, geocoder) => {
        const fromResult = getCountryCodeFromGeocodeResult(result);
        if (fromResult) return fromResult;

        const lat = result?.geometry?.location?.lat?.();
        const lng = result?.geometry?.location?.lng?.();
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return '';

        return await new Promise((resolve) => {
            geocoder.geocode({ location: { lat, lng } }, (geoResults, status) => {
                if (status !== 'OK' || !Array.isArray(geoResults)) {
                    resolve('');
                    return;
                }
                resolve(getCountryCodeFromGeocodeResult(geoResults[0]));
            });
        });
    };

    const resolveCountryCodeFromPrediction = async (prediction, geocoder) => {
        const placeId = prediction?.place_id;
        const address = prediction?.description;
        if (!placeId && !address) return '';

        return await new Promise((resolve) => {
            const request = placeId ? { placeId } : { address };
            geocoder.geocode(request, (geoResults, status) => {
                if (status !== 'OK' || !Array.isArray(geoResults)) {
                    resolve('');
                    return;
                }

                resolve(getCountryCodeFromGeocodeResult(geoResults[0]));
            });
        });
    };

    const matchesSelectedCountry = (countryCode) => {
        return (
            selectedCountryCodes.length === 0 ||
            selectedCountryCodes.includes(countryCode)
        );
    };

    const onSelectPredicition = async (prediction) => {
        /*global google*/
        /*eslint no-undef: "error"*/
        const service = new google.maps.places.PlacesService(
            document.createElement('div')
        );
        const geocoder = new google.maps.Geocoder();
        const search = prediction.description || prediction.label || '';
        if (!search) return;
        if (
            (selectedCountries || []).length > 0 &&
            selectedCountryCodes.length === 0
        ) {
            dispatch(
                notificationsActions.setNotification({
                    type: 'warning',
                    title: t(
                        'sections.campaigns.new-campaign.wizard-basics.location.errors.places-title'
                    ),
                    message:
                        'No se pudo determinar el código ISO2 del país seleccionado.',
                })
            );
            return;
        }

        if (notExists(search)) {
            const queryCountry =
                selectedCountryNames.length === 1 &&
                !search
                    .toLowerCase()
                    .includes(selectedCountryNames[0].toLowerCase())
                    ? selectedCountryNames[0]
                    : '';
            const request = {
                query: queryCountry ? `${search}, ${queryCountry}` : search,
                fields: ['name', 'formatted_address', 'geometry'],
            };
            if (selectedCountryCodes.length === 1) {
                request.region = selectedCountryCodes[0].toLowerCase();
            }
            const rad =
                Number.isFinite(Number(radius)) && Number(radius) > 0
                    ? Number(radius)
                    : 400;
            const zones = [];
            service.textSearch(request, (results = [], status) => {
                if (status === 'OK') {
                    (async () => {
                        let zone = {
                            active: true,
                            search,
                            manual: false,
                            color: randomRgb,
                            results: [],
                            label: search,
                            colors: colorsArr,
                        };

                        const recursiveFunction = () => {
                            if (checkColorAlreadyExist(zone) === true) {
                                zone.color = generateColorAgain();
                                recursiveFunction();
                            }
                        };
                        recursiveFunction();

                        const countryResolvedResults = await Promise.all(
                            results.map(async (result) => ({
                                result,
                                countryCode: await resolveCountryCode(
                                    result,
                                    geocoder
                                ),
                            }))
                        );

                        const filteredResults =
                            selectedCountryCodes.length === 0
                                ? countryResolvedResults.map(
                                      ({ result }) => result
                                  )
                                : countryResolvedResults
                                      .filter(({ countryCode }) => {
                                          return matchesSelectedCountry(
                                              countryCode
                                          );
                                      })
                                      .map(({ result }) => result);

                        for (let r of filteredResults) {
                            const res = {
                                ref:
                                    r.place_id ||
                                    r.reference ||
                                    `${search}-${r.geometry.location.lat()}-${r.geometry.location.lng()}`,
                                lat: r.geometry.location.lat(),
                                lng: r.geometry.location.lng(),
                                radius: rad,
                                color: zone.color,
                            };
                            zone.results.push(res);
                        }
                        if (!zone.results.length) {
                            dispatch(
                                notificationsActions.setNotification({
                                    type: 'warning',
                                    title: t(
                                        'sections.campaigns.new-campaign.wizard-basics.location.errors.places-title'
                                    ),
                                    message:
                                        'La búsqueda está limitada al país seleccionado.',
                                })
                            );
                            return;
                        }
                        zones.push(zone);
                        const newLocation = Object.assign({}, reduxLocation);
                        newLocation.zones = reduxLocation.zones.concat(zones);
                        dispatch(newCampaignActions.setLocation(newLocation));
                        dispatch(newCampaignActions.setMapLocations(newLocation));
                    })();
                } else {
                    dispatch(
                        notificationsActions.setNotification({
                            type: 'error',
                            title: t(
                                'sections.campaigns.new-campaign.wizard-basics.location.errors.places-title'
                            ),
                            message: t(
                                'sections.campaigns.new-campaign.wizard-basics.location.errors.places-status',
                                { status }
                            ),
                        })
                    );
                }
            });
        }
    };

    const loadOptions = (inputValue, callback) => {
        /*global google*/
        /*eslint no-undef: "error"*/
        if (inputValue === '') {
            callback([]);
        } else {
            if (selectedCountries.length > 0 && selectedCountryCodes.length === 0) {
                callback([]);
                return;
            }
            clearTimeout(debounceLoadOptions);
            debounceLoadOptions = setTimeout(() => {
                const service = new google.maps.places.AutocompleteService();
                const geocoder = new google.maps.Geocoder();
                const request = {
                    input: inputValue,
                };
                service.getQueryPredictions(request, async (res) => {
                    if (!res) {
                        callback([]);
                        return;
                    }

                    const predictionsWithCountry = await Promise.all(
                        res.map(async (prediction) => ({
                            prediction,
                            countryCode: await resolveCountryCodeFromPrediction(
                                prediction,
                                geocoder
                            ),
                        }))
                    );
                    const filteredPredictions =
                        selectedCountryCodes.length === 0
                            ? predictionsWithCountry
                            : predictionsWithCountry.filter(({ countryCode }) => {
                                  return (
                                      countryCode &&
                                      matchesSelectedCountry(countryCode)
                                  );
                              });
                    const predictions = filteredPredictions.map(
                        ({ prediction }) => ({
                            ...prediction,
                            label: prediction.description,
                            value: prediction.description,
                        })
                    );
                    const typedSearchOption =
                        selectedCountryCodes.length > 0 &&
                        inputValue.trim().length >= 3
                            ? [
                                  {
                                      description: inputValue.trim(),
                                      label: inputValue.trim(),
                                      value: inputValue.trim(),
                                  },
                              ]
                            : [];
                    callback([...typedSearchOption, ...predictions]);
                });
            }, 300);
        }
    };

    return (
        <AsyncSelect
            cacheOptions
            loadOptions={loadOptions}
            onChange={onSelectPredicition}
            placeholder={t(
                'sections.campaigns.new-campaign.wizard-basics.location.nearPlace-placeholder'
            )}
            noOptionsMessage={() =>
                t(
                    'sections.campaigns.new-campaign.wizard-basics.location.nearPlace-placeholder'
                )
            }
            value={null}
            inputId={id}
        />
    );
};

export default InputZoneSearch;
